import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Frame } from '@shopify/polaris';
import JobProgressPill from './JobProgressPill';
import GenerationLimitToast from './GenerationLimitToast';
import UpgradeToast from './UpgradeToast';
import { PlansModalHost } from './PlansUpgradeModal';
import { PlanLimitOverlayHost, QuotaToastHost } from './PlanLimitOverlay';
import ReferralWelcomeBanner from './ReferralWelcomeBanner';
import AffiliateWelcomeBanner from './AffiliateWelcomeBanner';
import { openPlansModal } from '../utils/plansModal';
import ChoosePlanScreen from './ChoosePlanScreen';
import { AccessLoadingScreen, AccessUnavailableScreen } from './AccessGateScreen';
import useBilling from '../hooks/useBilling';
import useAccess from '../hooks/useAccess';
import useNotifications from '../hooks/useNotifications';
import ReviewPromptCard from './ReviewPromptCard';
import SupportWidget from './SupportWidget';
import FeatureRequestButton from './FeatureRequest';
import ConnectivityBanner from './ConnectivityBanner';
import SessionBanner from './SessionBanner';
import ContentRestoreBanner from './ContentRestoreBanner';
import ErrorBoundary from './ErrorBoundary';
import { ConfirmDialogHost } from './ConfirmDialog';
import useShopify from '../hooks/useShopify';
import { useTranslation } from '../i18n';
import { apiFetch } from '../lib/api';
import openTop from '../lib/openTop';
import { openNewTab, turnOnAiEditor } from '../lib/themeEditorTurnOn';
import logo from '../assets/logo.png';

// "New Product" lives inside Products now — the /new-product route
// still works, it's just reached via the Create New Product button.
// Labels are i18n keys resolved with t() at render time.
// `requires` is a billing.features gate key (from the unified plan
// config): when it's false for the shop's plan the item shows locked
// and clicking opens the plans/upgrade modal instead of navigating.
const NAV_LINKS = [
  { labelKey: 'nav.dashboard',    to: '/',            match: (p) => p === '/' },
  { labelKey: 'nav.products',     to: '/products',    match: (p) => p.startsWith('/product') || p === '/new-product' || p.startsWith('/generations/') },
  { labelKey: 'nav.website',      to: '/website',     match: (p) => p === '/website' },
  { labelKey: 'nav.advertorials', to: '/advertorials', match: (p) => p === '/advertorials' },
  { labelKey: 'nav.ads',          to: '/ads',         match: (p) => p === '/ads', requires: 'adsManager' },
  { labelKey: 'nav.splitTesting', to: '/split-testing', match: (p) => p === '/split-testing', requires: 'splitTests' },
  { labelKey: 'nav.findUpsells', to: '/upsell', match: (p) => p === '/upsell', requires: 'upsells' },
  { labelKey: 'nav.findProducts', to: '/find-products', match: (p) => p === '/find-products' },
  { labelKey: 'nav.aiChat',       to: '/chat',        match: (p) => p === '/chat', requires: 'aiChat' },
  // Refer & Earn hub (peer referral + affiliate program) — also owns /referrals and /affiliate/*.
  { labelKey: 'nav.referAndEarn', to: '/refer-and-earn', match: (p) => p === '/refer-and-earn' || p === '/referrals' || p.startsWith('/affiliate') },
  { labelKey: 'nav.settings',     to: '/settings',    match: (p) => p === '/settings' || p === '/feedback' },
];

// The navigation dropdown lists EVERY page, always, in this order, under
// small muted group labels (`labelKey` null = no label). Nothing is ever
// folded away or hidden at any width.
const NAV_GROUPS = [
  { labelKey: null,                   paths: ['/'] },
  { labelKey: 'nav.groups.create',    paths: ['/products', '/website', '/advertorials'] },
  { labelKey: 'nav.groups.grow',      paths: ['/ads', '/split-testing', '/upsell', '/find-products'] },
  { labelKey: 'nav.groups.assistant', paths: ['/chat'] },
  { labelKey: 'nav.groups.account',   paths: ['/refer-and-earn', '/settings'] },
].map(group => ({ ...group, links: group.paths.map(to => NAV_LINKS.find(link => link.to === to)) }));
const MENU_LINKS = NAV_GROUPS.flatMap(group => group.links);

// Shopify's own left sidebar: App Bridge's <ui-nav-menu> relays the
// anchors inside it to the admin ("Autonoma AI" → pages). The custom
// element only exists when the App Bridge script is loaded (i.e. inside
// the admin) — outside it (local dev, tests) nothing is rendered.
const navMenuDefined = () =>
  typeof window !== 'undefined' && !!window.customElements?.get?.('ui-nav-menu');

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const billing = useBilling();
  const { shop, loading: sessionLoading, authenticated, error: sessionError, reinstallUrl } = useShopify();
  const access = useAccess(shop);
  const { t } = useTranslation();
  const { notifications, unreadCount, markRead, markActioned, applyLocal } = useNotifications(shop);
  const [panelOpen, setPanelOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const bellRef = useRef(null);

  // Sub-routes without their own nav item (/generations/:id, /feedback,
  // /affiliate/*) light up their parent section instead of falsely
  // showing "Dashboard".
  const currentPage = NAV_LINKS.find(link => link.match(location.pathname)) || NAV_LINKS[0];
  const isLocked = (link) => !!(link.requires && billing.features && billing.features[link.requires] === false);

  // Navigation dropdown. `menuRef` wraps trigger + menu (outside-click
  // detection); `itemRefs` holds the menu items in display order so the
  // arrow keys can move focus between them (roving focus: the trigger is
  // the only tab stop, items are tabIndex -1).
  const menuRef = useRef(null);
  const menuTriggerRef = useRef(null);
  const itemRefs = useRef([]);
  const focusItem = useCallback((index) => {
    const items = itemRefs.current.filter(Boolean);
    if (!items.length) return;
    const i = ((index % items.length) + items.length) % items.length; // wraps both ways
    items[i].focus();
  }, []);
  // Opening from the keyboard lands focus on the current page's item
  // (Enter/Space) or the first/last one (ArrowDown/ArrowUp); a mouse
  // click leaves focus on the trigger.
  const pendingFocus = useRef(null);
  useEffect(() => {
    if (!menuOpen || pendingFocus.current == null) return;
    const where = pendingFocus.current;
    pendingFocus.current = null;
    if (where === 'current') focusItem(Math.max(0, MENU_LINKS.indexOf(currentPage)));
    else focusItem(where === 'last' ? -1 : 0);
  }, [menuOpen, currentPage, focusItem]);
  // Route change (dropdown item, sidebar, in-page link) → menu closed.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  const onTriggerKeyDown = (e) => {
    const wants = e.key === 'ArrowDown' ? 'first'
      : e.key === 'ArrowUp' ? 'last'
        : (e.key === 'Enter' || e.key === ' ') ? 'current' : null;
    if (!wants) return;
    e.preventDefault(); // we own Enter/Space here (no second, native click)
    if (!menuOpen) { pendingFocus.current = wants; setMenuOpen(true); return; }
    if (wants === 'current') { setMenuOpen(false); return; } // Enter/Space again → closes
    focusItem(wants === 'last' ? -1 : 0); // arrows on an open menu → move into it
  };
  const onMenuKeyDown = (e) => {
    const items = itemRefs.current.filter(Boolean);
    const at = items.indexOf(document.activeElement);
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); focusItem(at + 1); break;
      case 'ArrowUp': e.preventDefault(); focusItem(at - 1); break;
      case 'Home': e.preventDefault(); focusItem(0); break;
      case 'End': e.preventDefault(); focusItem(-1); break;
      case 'Tab': setMenuOpen(false); menuTriggerRef.current?.focus?.(); break; // Tab leaves from the trigger
      default: break;
    }
  };
  const activateLink = (link) => {
    setMenuOpen(false);
    // Locked feature → the plans/upgrade modal is the CTA, not the
    // (gated) page itself.
    if (isLocked(link)) openPlansModal({ highlight: link.requires });
    else navigate(link.to);
  };

  // Shopify admin sidebar (<ui-nav-menu>): rendered once App Bridge has
  // defined the element. The script is synchronous in <head>, so this is
  // known at mount; `whenDefined` covers a late definition.
  const [navMenuReady, setNavMenuReady] = useState(navMenuDefined);
  useEffect(() => {
    if (navMenuReady || typeof window === 'undefined' || !window.customElements?.whenDefined) return undefined;
    let alive = true;
    window.customElements.whenDefined('ui-nav-menu').then(() => { if (alive) setNavMenuReady(true); }, () => {});
    return () => { alive = false; };
  }, [navMenuReady]);

  // Deep-link from the Meta OAuth callback: the backend appends
  // ?page=settings to the embedded-app URL after a successful (or
  // failed) connect so the merchant lands directly on the page where
  // they can pick their ad account / page / pixel. Run-once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') === 'settings' || params.get('redirect') === 'settings') {
      // Forward the Meta OAuth error code (if any) so Settings can show
      // a real banner — previously the code was silently dropped here
      // and a failed connect looked identical to a successful one.
      const metaError = params.get('meta_error');
      navigate(metaError
        ? `/settings?meta_error=${encodeURIComponent(metaError)}`
        : '/settings', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the dropdown when the user clicks anywhere outside of it
  // (or hits Escape). Cleaner than tracking blur on the button.
  useEffect(() => {
    if (!panelOpen) return undefined;
    const onDocClick = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setPanelOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setPanelOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [panelOpen]);

  // Nav dropdown: Escape closes it and returns focus to its trigger; a
  // click anywhere outside the trigger/menu closes it (and still reaches
  // whatever was clicked — no backdrop).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuTriggerRef.current?.focus?.();
      }
    };
    const onDocMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDocMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [menuOpen]);

  const planLabel = billing.trialActive
    ? `Free Trial · ${billing.trialDaysLeft}d left`
    : (billing.planName || null);
  const badgeClass = billing.trialActive
    ? 'au-plan-badge au-plan-badge--trial'
    : 'au-plan-badge';

  const handleAction = async (notif) => {
    // Theme-editor embed nudge: the SAME "Turn on AI editor" flow as
    // Settings and the dashboard card (POST /turn-on switches the AI
    // editor on, records the click; the editor opens in a NEW TAB and the
    // connect code is copied). Nothing is pre-fetched here, so the tab
    // opens right after /turn-on answers (best effort). Read (not
    // actioned) — the backend marks it actioned once the embed is ON.
    if (notif.type === 'embed_nudge') {
      setPanelOpen(false);
      markRead(notif.id);
      try {
        await turnOnAiEditor();
      } catch {
        // Could not flip the switch — the plain deep link still gets the
        // merchant into the editor.
        try {
          await apiFetch('/api/theme-editor-admin/nudge/clicked', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
        } catch { /* the deep link still works */ }
        openNewTab(notif.action?.route);
      }
      return;
    }
    await markActioned(notif.id);
    setPanelOpen(false);
    // "Let's do it": suggestions with a chat execution block hand off
    // to the AI chat operator, which executes end-to-end (confirm-first).
    if (notif.execution?.kind === 'chat_prompt') {
      navigate(`/chat?fromSuggestion=${notif.id}`);
      return;
    }
    const route = notif.action?.route;
    if (!route) return;
    // Admin paths (action.target === 'top') navigate the Shopify admin
    // itself; external http(s) targets (e.g. the storefront cart after a
    // cart upgrade) open in a new tab; app routes navigate in-app.
    if (notif.action?.target === 'top') openTop(route);
    else if (/^https?:\/\//i.test(route)) window.open(route, '_blank', 'noopener');
    else navigate(route);
  };

  // ── SUBSCRIPTION WALL (deny by default) ─────────────────────────────
  // Shopify's left sidebar ("Autonoma AI" → every page). The anchors are
  // react-router Links: App Bridge re-dispatches the sidebar click on
  // them, so navigation stays client-side. Locked pages link as normal —
  // the page shows its own upgrade prompt. The element is hidden in-app
  // (index.css); Dashboard is the home link (rel="home"), which the
  // sidebar shows as the app name. Rendered in EVERY access state: while
  // the wall is up each sidebar item routes to the wall again.
  const navMenu = navMenuReady ? (
    <ui-nav-menu data-testid="admin-nav-menu">
      {NAV_LINKS.map(link => (
        <Link key={link.to} to={link.to} rel={link.to === '/' ? 'home' : undefined}>
          {t(link.labelKey)}
        </Link>
      ))}
    </ui-nav-menu>
  ) : null;

  // Nothing of the app renders until /api/billing/access-state says the
  // shop has a verified, approved subscription (or comp / reviewer):
  //   loading      → neutral screen (the dashboard never flashes first)
  //   no plan      → ChoosePlanScreen on EVERY route (deep links and every
  //                  sidebar item included)
  //   blocked      → the reactivate screen (cancelled / frozen / declined /
  //                  expired / pending), Settings → Billing included
  //   unavailable  → "try again" (the check could not run; never the app)
  // A dead session (401) is not walled here — the SessionBanner below
  // owns the reconnect / reinstall flow.
  const wall = !shop ? null
    : access.loading ? 'loading'
      : access.state === 'unauthenticated' ? null
        : access.noPlan ? 'choose'
          : access.blocked ? 'reactivate'
            : access.unavailable ? 'unavailable'
              : null;
  if (wall === 'loading') return <>{navMenu}<AccessLoadingScreen /></>;
  if (wall === 'choose') return <>{navMenu}<ChoosePlanScreen onRecheck={access.refresh} /></>;
  if (wall === 'reactivate') {
    return <>{navMenu}<ChoosePlanScreen mode="reactivate" message={access.message} reason={access.reason} onRecheck={access.refresh} /></>;
  }
  if (wall === 'unavailable') return <>{navMenu}<AccessUnavailableScreen message={access.message} onRetry={access.refresh} /></>;

  return (
    <Frame>
      <nav className="au-tabnav" aria-label="Primary">
        {navMenu}
        {/* Header order: [Logo][Plan] ….. [✨ Request a feature][Navigation dropdown ▾][Support][Notifications] */}
        <div
          className="au-tabnav__inner"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 20px',
            gap: '18px',
            flexWrap: 'nowrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '0 0 auto' }}>
            <button
              type="button"
              className="au-tabnav__logo"
              onClick={() => navigate('/')}
              aria-label="Autonoma AI — Dashboard"
              style={{ marginRight: 0 }}
            >
              <img
                src={logo}
                alt="Autonoma AI"
                style={{ height: '40px', width: 'auto', display: 'block' }}
              />
            </button>

            {planLabel && (
              <span className={badgeClass} aria-label={`Current plan: ${planLabel}`}>
                <span className="au-plan-badge__dot" aria-hidden="true" />
                {planLabel}
              </span>
            )}
          </div>

          <div className="au-tabnav__right">
          {/* "Request a feature" — immediately LEFT of the navigation dropdown. */}
          <div className="au-tabnav__request" style={{ display: 'flex', flex: '0 0 auto' }}>
            <FeatureRequestButton />
          </div>

          {/* ONE navigation dropdown that always lists every page. The
              trigger shows the current page's name; the menu is grouped
              (Dashboard · Create · Grow · Assistant · Account), compact
              enough to fit a 768px-tall window, and scrolls inside
              itself if it ever can't. */}
          <div className="au-navdd" ref={menuRef}>
            <button
              type="button"
              ref={menuTriggerRef}
              className="au-navdd__trigger"
              onClick={() => setMenuOpen(o => !o)}
              onKeyDown={onTriggerKeyDown}
              aria-haspopup="true"
              aria-expanded={menuOpen}
              aria-label={`${t('ui.nav.menuLabel')}: ${t(currentPage.labelKey)}`}
            >
              <span>{t(currentPage.labelKey)}</span>
              <span aria-hidden="true" className={`au-navdd__caret${menuOpen ? ' au-navdd__caret--open' : ''}`}>▼</span>
            </button>

            {menuOpen && (
              <div role="menu" aria-label={t('ui.nav.menuLabel')} className="au-navdd__menu" onKeyDown={onMenuKeyDown}>
                {NAV_GROUPS.map((group, gi) => {
                  const labelId = group.labelKey ? `au-navdd-group-${gi}` : undefined;
                  return (
                    <div key={gi} role="group" aria-labelledby={labelId} className="au-navdd__group">
                      {group.labelKey && (
                        <div id={labelId} className="au-navdd__group-label">{t(group.labelKey)}</div>
                      )}
                      {group.links.map(link => {
                        const isActive = link === currentPage;
                        const locked = isLocked(link);
                        return (
                          <button
                            key={link.to}
                            ref={(el) => { itemRefs.current[MENU_LINKS.indexOf(link)] = el; }}
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            className={`au-navdd__item${isActive ? ' au-navdd__item--active' : ''}${locked ? ' au-navdd__item--locked' : ''}`}
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => activateLink(link)}
                            title={locked ? t('ui.nav.lockedTitle', { name: t(link.labelKey) }) : undefined}
                          >
                            {t(link.labelKey)}
                            {locked && <span className="au-navdd__lock" aria-hidden="true">🔒</span>}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Support widget — chat-style "message us" panel, sits left
              of the notifications bell (the menu shifts left to fit). */}
          <SupportWidget shop={shop} />

          <div ref={bellRef} style={{ position: 'relative' }}>
            {/* Gradient SVG bell — no circle outline */}
            <button
              type="button"
              onClick={() => setPanelOpen(o => !o)}
              aria-haspopup="true"
              aria-expanded={panelOpen}
              aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
              style={{
                position: 'relative',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="url(#bellGradient)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="bellGradient" x1="0" y1="0" x2="24" y2="24">
                    <stop offset="0%" stopColor="#6B4EE8" />
                    <stop offset="100%" stopColor="#4A90E2" />
                  </linearGradient>
                </defs>
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    minWidth: '16px',
                    height: '16px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #6B4EE8 0%, #4A90E2 100%)',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                  }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {panelOpen && (
              <div className="au-notif-panel" role="dialog" aria-label={t('nav.notifications')}>
                <div className="au-notif-panel__header">
                  <span>{t('nav.notifications')}</span>
                  <button
                    type="button"
                    className="au-notif-panel__close"
                    onClick={() => setPanelOpen(false)}
                    aria-label="Close notifications"
                  >
                    ×
                  </button>
                </div>
                <div className="au-notif-panel__list" role="list">
                  {notifications.length === 0 ? (
                    <div className="au-notif-empty" data-testid="notifications-empty">
                      <span className="au-notif-empty__icon" aria-hidden="true">🔔</span>
                      <div className="au-notif-empty__title">{t('nav.caughtUp')}</div>
                      <div>{t('nav.noNotificationsBody')}</div>
                      <button
                        type="button"
                        className="au-notif-item__action"
                        onClick={() => { setPanelOpen(false); navigate('/new-product'); }}
                      >
                        {t('dashboard.primaryAction')} →
                      </button>
                    </div>
                  ) : (
                    notifications.map(notif => (
                      // A <div>, not a <button>: the row used to be a button
                      // containing the action button (invalid HTML — screen
                      // readers flatten it and keyboard users can't reach
                      // the inner CTA). Mark-read is now its own button on
                      // the title; the CTA is a sibling.
                      <div
                        key={notif.id}
                        role="listitem"
                        className={`au-notif-item${notif.read ? '' : ' au-notif-item--unread'}`}
                      >
                        <span className="au-notif-item__icon" aria-hidden="true">
                          {notif.icon || '✨'}
                        </span>
                        {notif.type === 'review_prompt' ? (
                          // App Store review prompt — 👍 👎 satisfaction
                          // gate (no generic mark-read: "Not now" is the
                          // dismissal, and it's recorded as one).
                          <div style={{ flex: 1 }}>
                            <ReviewPromptCard
                              notif={notif}
                              variant="bell"
                              onLocalUpdate={applyLocal}
                              onClose={() => setPanelOpen(false)}
                            />
                          </div>
                        ) : (
                        <div style={{ flex: 1 }}>
                          <button
                            type="button"
                            className="au-notif-item__title au-notif-item__title--btn"
                            onClick={() => markRead(notif.id)}
                            aria-label={notif.read ? notif.title : `${notif.title} (mark as read)`}
                          >
                            {notif.title}
                          </button>
                          <div className="au-notif-item__msg">{notif.message}</div>
                          {(notif.action || notif.execution?.kind === 'chat_prompt') && !notif.actioned && (
                            <button
                              type="button"
                              className="au-notif-item__action"
                              onClick={() => handleAction(notif)}
                            >
                              {notif.execution?.kind === 'chat_prompt'
                                ? 'Let\u2019s do it ✨'
                                : `${notif.action.label} →`}
                            </button>
                          )}
                          {notif.actioned && (
                            <span className="au-notif-item__done">✅ Done</span>
                          )}
                        </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </nav>

      {/* Offline / rate-limited / session-lost — one banner for the whole app. */}
      <ConnectivityBanner reinstallUrl={reinstallUrl} />

      {/* A dead OAuth record (uninstall → reinstall, token wipe) used to
          render the full dashboard with every widget quietly failing;
          only /products showed the reconnect banner. Now the shell does. */}
      {!sessionLoading && shop && !authenticated && (
        <div style={{ padding: '12px 16px 0' }}>
          <SessionBanner error={sessionError} authenticated={authenticated} reinstallUrl={reinstallUrl} />
        </div>
      )}

      {/* Referred merchant's 7-day welcome ("your referrer gave you a
          bonus credit pack") — every page, dismissible, backend-timed. */}
      {!sessionLoading && shop && authenticated && (
        <>
          {/* "Restoring your pages…" after a reactivation — dismissible,
              polls access-state until the detached restore completes. */}
          <ContentRestoreBanner contentRestore={access.contentRestore} reload={access.reload} />
          <ReferralWelcomeBanner shop={shop} />
          {/* Affiliate-referred merchant's 7-day welcome ("you arrived via
              [affiliate]'s link — enjoy your welcome bonus"). */}
          <AffiliateWelcomeBanner shop={shop} />
        </>
      )}

      <div className="au-content">
        {/* Only reached with verified access — the wall above owns every
            other state (no per-route exceptions, Settings included). */}
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </div>

      {/* Floating progress for background jobs — visible on every page. */}
      <JobProgressPill shop={shop} />

      {/* Top popup for the 4-concurrent-generations limit (8s). */}
      <GenerationLimitToast />
      <UpgradeToast />
      {/* Monthly quota cap (pre-check or any server 403) → the plan overlay
          over the current screen; near-limit / "plan upgraded" toast. */}
      <PlanLimitOverlayHost plan={billing.loading ? null : (billing.plan || 'none')} />
      <QuotaToastHost />
      <PlansModalHost />
      <ConfirmDialogHost />

      {/* TrialBanner removed: its "trial expired" overlay belonged to the
          legacy install-age trial. Lockouts are owned by the subscription
          wall above (ChoosePlanScreen: no plan / reactivate). */}
    </Frame>
  );
}
